import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import AgeOptions from 'Components/Utility/Age/AgeOptions';
import AgeRatings from 'Components/Utility/Age/AgeRatings';

const messages = defineMessages({
  day: {
    id: 'DateOfBirth.day',
    defaultMessage: 'Day',
  },
  month: {
    id: 'DateOfBirth.month',
    defaultMessage: 'Month',
  },
  year: {
    id: 'DateOfBirth.year',
    defaultMessage: 'Year',
  },
  dayError: {
    id: 'DateOfBirth.error.day',
    defaultMessage: 'Please select a Day',
  },
  monthError: {
    id: 'DateOfBirth.error.month',
    defaultMessage: 'Please select a Month',
  },
  yearError: {
    id: 'DateOfBirth.error.year',
    defaultMessage: 'Please select a Year',
  },
});

const AgeGating = ({
  submit,
  failed,
  ratings,
  intl,
  lazyLoad,
  locked,
  hitcard,
  failedPreviously,
  authenticated,
}) => {
  const { formatMessage } = intl;
  const [inputs, setInputs] = useState({
    year: '',
    month: '',
    day: '',
  });

  const [error, setError] = useState(null);

  const onChange = (event) => {
    event.persist();
    setInputs({ ...inputs, [event.target.name]: event.target.value });
  };

  const onSubmit = (event) => {
    event.preventDefault();

    if (!inputs.day) {
      return setError(formatMessage(messages.dayError));
    }

    if (!inputs.month) {
      return setError(formatMessage(messages.monthError));
    }

    if (!inputs.year) {
      return setError(formatMessage(messages.yearError));
    }

    setError(null);

    return submit(inputs.year, inputs.month, inputs.day);
  };

  const years = AgeOptions.getYears(formatMessage(messages.year));
  const months = AgeOptions.getMonths(formatMessage(messages.month));
  const days = AgeOptions.getDays(formatMessage(messages.day));

  if (failedPreviously) {
    return (
      <div className="age-gating text-center">
        <h2>
          <strong>
            <FormattedMessage id="agegating.title" defaultMessage="Age Restricted Content" />
          </strong>
        </h2>
        <hr />
        <AgeRatings lazyLoad={lazyLoad} ratings={ratings} hitcard={hitcard} />
        <p className="pt-2">
          <strong>
            {failed &&
              <FormattedMessage
                id="agegating.you-have-failed.now"
                defaultMessage="You have entered an age which is too young to view this content. You must sign in or create an account to change this age in your account. You can do this within the 'Login & Security' section."
              />
            }
            {!failed &&
              <FormattedMessage
                id="agegating.you-have-failed.before"
                defaultMessage="You have previously entered an age which is too young to view this content. You must sign in or create an account to change this age in your account. You can do this within the 'Login & Security' section."
              />
            }
          </strong>
        </p>
        {(authenticated || !authenticated) &&
          <Link
            to="/account/login"
            className="btn-block submit-age btn btn-primary"
          >
            <FormattedMessage id="agegating.button.account" defaultMessage="Go to account" />
          </Link>
        }
      </div>
    );
  }

  return (
    <React.Fragment>
      {!locked &&
        <div className={`age-gating m-2 text-center ${hitcard ? 'full-age-gate' : ''}`}>
          <h2>
            <strong>
              <FormattedMessage id="agegating.title" defaultMessage="Age Restricted Content" />
            </strong>
          </h2>
          <hr />
          <AgeRatings lazyLoad={lazyLoad} ratings={ratings} hitcard={hitcard} />
          <p className="pt-2">
            <strong>
              <FormattedMessage id="agegating.pleaseenter" defaultMessage="Please enter your date of birth below." />
            </strong>
          </p>
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <div className="row">
                <div className="col">
                  <label hidden>{formatMessage(messages.day)}</label>
                  <select
                    aria-label="age-day"
                    className="w-100 form-control"
                    type="select"
                    placeholder="Day"
                    value={inputs.day}
                    name="day"
                    autoComplete="bday-day"
                    onChange={onChange}
                  >
                    <option value="">{formatMessage(messages.day)}</option>
                    {days.map(d =>
                      <option key={d} value={d}>{d}</option>,
                    )}
                  </select>
                </div>

                <div className="col">
                  <label hidden>{formatMessage(messages.month)}</label>
                  <select
                    aria-label="age-month"
                    className="w-100 form-control"
                    type="select"
                    placeholder="Month"
                    value={inputs.month}
                    name="month"
                    autoComplete="bday-month"
                    onChange={onChange}
                  >
                    <option value="">{formatMessage(messages.month)}</option>
                    {months.map(m =>
                      <option key={m} value={m}>{m}</option>,
                    )}
                  </select>
                </div>

                <div className="col">
                  <label hidden>{formatMessage(messages.year)}</label>
                  <select
                    aria-label="age-year"
                    className="w-100 form-control"
                    type="select"
                    placeholder="Years"
                    value={inputs.year}
                    name="year"
                    autoComplete="bday-year"
                    onChange={onChange}
                  >
                    <option value="">{formatMessage(messages.year)}</option>
                    {years.map(y =>
                      <option key={y} value={y}>{y}</option>,
                    )}
                  </select>
                </div>
              </div>

              <p className="confirmation mt-3">
                <FormattedMessage id="agegating.confirm" defaultMessage="By clicking continue, I confirm that I am of legal age to view this game." />
              </p>

              <button
                type="submit"
                className="btn-block submit-age btn btn-primary"
              >
                <FormattedMessage id="agegating.continue" defaultMessage="Continue" />
              </button>
              {failed &&
                <div className="mt-3">
                  <div className="failed-age p-1 mb-1 w-100">
                    <strong><FormattedMessage id="agegating.fail" defaultMessage="You are not old enough to view this content" /></strong>
                  </div>
                </div>
              }
              {error &&
                <div className="mt-3">
                  <div className="failed-age p-1 mb-1 w-100">
                    <strong>{error}</strong>
                  </div>
                </div>
              }
            </div>
          </form>
        </div>
      }
      {locked &&
        <AgeRatings lazyLoad={lazyLoad} ratings={ratings} />
      }
    </React.Fragment>
  );
};

AgeGating.propTypes = {
  submit: PropTypes.func.isRequired,
  failed: PropTypes.bool,
  failedPreviously: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  ratings: PropTypes.shape({
    ACB: PropTypes.number,
    ESRB: PropTypes.number,
    PEGI: PropTypes.number,
    USK: PropTypes.number,
  }),
  locked: PropTypes.bool,
  hitcard: PropTypes.bool,
  authenticated: PropTypes.bool,
};

AgeGating.defaultProps = {
  failed: false,
  failedPreviously: false,
  lazyLoad: true,
  ratings: {},
  locked: false,
  hitcard: false,
  authenticated: false,
};

export default injectIntl(AgeGating);
